import React, { useState } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import Typography from '@mui/material/Typography';
import { CloudUpload, CheckCircle, Error } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Papa from 'papaparse';
import PropTypes from 'prop-types';

const CSVDropzone = ({
  onProcess,
  onError,
  style = {},
  className,
  disabled = false,
  maxFiles = 1,
  showPreview = true,
  acceptMessage = 'Drag and drop a CSV file here, or click to browse files',
  processingMessage = 'Processing...',
  clearButtonText = 'Clear'
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState(null);

  const onDrop = async (acceptedFiles) => {
    try {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        
        // Check if it's a CSV file by extension
        if (file.name.toLowerCase().endsWith('.csv')) {
          setUploadedFile(file);
          setProcessingError(null);
          setProcessedData(null);
          setCsvHeaders(null);
          setProcessing(true);
          
          // Automatically process the CSV file
          await processCSVFile(file);
        } else {
          const errorMsg = 'Please upload a CSV file (.csv extension)';
          setProcessingError(errorMsg);
          if (onError) onError(errorMsg);
        }
      }
    } catch (error) {
      const errorMsg = 'Error processing file. Please try again.';
      console.error('Error in onDrop:', error);
      setProcessingError(errorMsg);
      if (onError) onError(errorMsg);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    disabled
  });

  const processCSVFile = async (file) => {
    setProcessingError(null);

    try {
      const text = await file.text();
      
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            const errorMsg = `CSV parsing errors: ${results.errors.map(e => e.message).join(', ')}`;
            setProcessingError(errorMsg);
            setProcessing(false);
            if (onError) onError(errorMsg);
            return;
          }

          if (results.data.length === 0) {
            const errorMsg = 'CSV file appears to be empty or has no valid data rows';
            setProcessingError(errorMsg);
            setProcessing(false);
            if (onError) onError(errorMsg);
            return;
          }

          // Store headers and data
          setCsvHeaders(results.meta.fields || []);
          setProcessedData(results.data);
          setProcessing(false);
          
          // Call the onProcess callback with the structured data
          if (onProcess) {
            onProcess({
              data: results.data,
              headers: results.meta.fields || [],
              rowCount: results.data.length,
              fileName: file.name,
              fileSize: file.size
            });
          }
        },
        error: (error) => {
          const errorMsg = `Failed to parse CSV: ${error.message}`;
          setProcessingError(errorMsg);
          setProcessing(false);
          if (onError) onError(errorMsg);
        }
      });
    } catch (error) {
      const errorMsg = 'Failed to read file. Please try again.';
      console.error('File reading failed:', error);
      setProcessingError(errorMsg);
      setProcessing(false);
      if (onError) onError(errorMsg);
    }
  };

  const clearFile = () => {
    setUploadedFile(null);
    setProcessedData(null);
    setCsvHeaders(null);
    setProcessingError(null);
  };

  return (
    <Wrapper style={style} className={className}>
      <UploadArea
        {...getRootProps()}
        isDragActive={isDragActive}
        hasFile={!!uploadedFile}
        isProcessing={processing}
        disabled={disabled}
      >
        <input {...getInputProps()} />
        <CloudUpload style={{ fontSize: 48, color: '#666', marginBottom: '16px' }} />
        {uploadedFile ? (
          <div style={{ textAlign: 'center' }}>
            <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              {uploadedFile.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
            </Typography>
            {processing && (
              <Typography variant="body2" color="primary" style={{ marginTop: '8px' }}>
                {processingMessage}
              </Typography>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
              {isDragActive ? 'Drop the CSV file here' : acceptMessage}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse files
            </Typography>
          </div>
        )}
      </UploadArea>

      {processingError && (
        <ErrorContainer>
          <Error style={{ color: '#f44336', marginRight: '8px' }} />
          <Typography variant="body2" color="error">
            {processingError}
          </Typography>
        </ErrorContainer>
      )}

      {processedData && showPreview && (
        <SuccessContainer>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <CheckCircle style={{ color: '#4CAF50', marginRight: '8px' }} />
            <Typography variant="body1" style={{ fontWeight: 'bold' }}>
              CSV Processed Successfully!
            </Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            Found {processedData.length} rows with {csvHeaders?.length} columns
          </Typography>
          {csvHeaders && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '4px' }}>
              Headers: {csvHeaders.join(', ')}
            </Typography>
          )}
        </SuccessContainer>
      )}

      {uploadedFile && (
        <ButtonContainer>
          <Button
            variant="outlined"
            onClick={clearFile}
            disabled={processing || disabled}
          >
            {clearButtonText}
          </Button>
        </ButtonContainer>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => {
    if (props.disabled) return '#ccc';
    if (props.isProcessing) return '#FF9800';
    if (props.isDragActive) return '#453DB7';
    if (props.hasFile) return '#4CAF50';
    return '#ccc';
  }};
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  cursor: ${props => props.disabled || props.isProcessing ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background-color: ${props => {
    if (props.disabled) return '#f5f5f5';
    if (props.isProcessing) return '#fff3e0';
    if (props.isDragActive) return '#f0f0ff';
    if (props.hasFile) return '#f0fff0';
    return '#fafafa';
  }};
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    border-color: ${props => props.disabled || props.isProcessing ? '#ccc' : '#453DB7'};
    background-color: ${props => props.disabled ? '#f5f5f5' : props.isProcessing ? '#fff3e0' : '#f0f0ff'};
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px;
  background-color: #ffebee;
  border-radius: 4px;
  border: 1px solid #ffcdd2;
`;

const SuccessContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  margin-top: 24px;
  text-align: center;
`;

CSVDropzone.propTypes = {
  onProcess: PropTypes.func,
  onError: PropTypes.func,
  style: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  maxFiles: PropTypes.number,
  showPreview: PropTypes.bool,
  acceptMessage: PropTypes.string,
  processingMessage: PropTypes.string,
  clearButtonText: PropTypes.string
};

CSVDropzone.defaultProps = {
  disabled: false,
  maxFiles: 1,
  showPreview: true,
  acceptMessage: 'Drag and drop a CSV file here, or click to browse files',
  processingMessage: 'Processing...',
  clearButtonText: 'Clear'
};

export { CSVDropzone }; 